import {Network} from "../../../network/Network";
import {HttpStatus} from "../../../network/status/HttpStatus";
import {ProductType} from "../../../models/product/ProductType";
import {ICommunityImagesModel} from "../../../models/community/images/ICommunityImagesModel";
import {INetworkComponent} from "../../../network/types/INetworkComponent";
import {ICommunityArticlesOverviews} from "../../../models/community/article/ICommunityArticlesOverviews";
import {ICommunityImagesOptimizeOutgoing} from "../../../models/community/images/ICommunityImagesOptimizeOutgoing";
import {
    ICommunityImagesSelectChallengeOutgoing
} from "../../../models/community/images/ICommunityImagesSelectChallengeOutgoing";
import {
    ICommunityImagesGenerateAltOutgoing
} from "../../../models/community/images/ICommunityImagesGenerateAltOutgoing";
import {ICommunityImagesPublishOutgoing} from "../../../models/community/images/ICommunityImagesPublishOutgoing";
import {
    ICommunityImagesChallengeEditOutgoing
} from "../../../models/community/images/ICommunityImagesChallengeEditOutgoing";
import {WebSocketCommunityEventName} from "../../../network/socket/names/WebSocketCommunityEventName";
import {arrayRemove} from "../../../sedestral/framework/utilities/ArrayRemove";
import {ICommunityImagesVersionSummaryModel} from "../../../models/community/images/version/ICommunityImagesVersionSummaryModel";
import {Services} from "../../Services";
import {Resources} from "../../../resources/Resources";

export class CommunityImagesService {

    private static updateEvents: ((images: ICommunityImagesModel) => void)[] = [];

    public static init(): void {
        Network.on(ProductType.COMMUNITY, WebSocketCommunityEventName.COMMUNITY_IMAGES_UPDATE, (data) => {
            this.updateEvents.forEach(callback => callback(data));
        });
    }

    public static onUpdateEvent(component: INetworkComponent, func: (images: ICommunityImagesModel) => void) {
        this.updateEvents.push(func);
        component.onRemove(() => arrayRemove(this.updateEvents, func));
    }

    public static async getLastOverviews(communityId: string, cursor?: string, component?: INetworkComponent, excludeRetouched?: boolean): Promise<ICommunityArticlesOverviews> {
        const request = await Network.post(ProductType.COMMUNITY, "/community/images/overviews", {
            communityId: communityId,
            cursor: cursor,
            excludeRetouched: excludeRetouched
        }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async getVersions(communityId: string, communityImagesId: string, component?: INetworkComponent): Promise<ICommunityImagesVersionSummaryModel[]> {
        const request = await Network.post(ProductType.COMMUNITY, "/community/images/versions", {
            communityId: communityId,
            communityImagesId: communityImagesId
        }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async get(communityId: string, communityImagesId: string, component?: INetworkComponent): Promise<ICommunityImagesModel> {
        const request = await Network.post(ProductType.COMMUNITY, "/community/images/get", {
            communityId: communityId,
            communityImagesId: communityImagesId
        }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async selectedVersion(communityId: string, communityImagesId: string, versionId: string, component?: INetworkComponent): Promise<ICommunityImagesModel> {
        const request = await Network.post(ProductType.COMMUNITY, "/community/images/selectedVersion", {
            communityId: communityId,
            communityImagesId: communityImagesId,
            versionId: versionId
        }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async create(communityId: string, id: string, component?: INetworkComponent): Promise<ICommunityImagesModel> {
        const request = await Network.post(ProductType.COMMUNITY, "/community/images/create", {
            communityId: communityId,
            id: id
        }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async update(communityId: string, communityImagesId: string, images: {fileId: string, alt?: string}[], component?: INetworkComponent): Promise<ICommunityImagesModel> {
        const request = await Network.postJson(ProductType.COMMUNITY, "/community/images/update", {
            communityId: communityId,
            communityImagesId: communityImagesId,
            images: images
        }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async uploadImage(communityId: string, communityImagesId: string, file: File, component?: INetworkComponent): Promise<ICommunityImagesModel> {
        let request = await Network.postFormData(ProductType.COMMUNITY, `/community/images/upload`, {
            communityId: communityId,
            communityImagesId: communityImagesId,
            file: file
        }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async optimize(data: ICommunityImagesOptimizeOutgoing, component?: INetworkComponent): Promise<ICommunityImagesModel> {
        const request = await Network.postJson(ProductType.COMMUNITY, "/community/images/optimize", data, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async selectChallenge(data: ICommunityImagesSelectChallengeOutgoing, component?: INetworkComponent): Promise<ICommunityImagesModel> {
        const request = await Network.postJson(ProductType.COMMUNITY, "/community/images/challenge/select", data, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async editChallengeImage(data: ICommunityImagesChallengeEditOutgoing, component?: INetworkComponent): Promise<ICommunityImagesModel> {
        const request = await Network.postJson(ProductType.COMMUNITY, "/community/images/challenge/edit", data, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async generateAlt(data: ICommunityImagesGenerateAltOutgoing, component?: INetworkComponent): Promise<ICommunityImagesModel> {
        const request = await Network.postJson(ProductType.COMMUNITY, "/community/images/alt/generate", data, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async publish(data: ICommunityImagesPublishOutgoing, component?: INetworkComponent): Promise<ICommunityImagesModel> {
        Services.handleErrors(component, [
            {status: HttpStatus.BAD_REQUEST, message: Resources.t("words.imagesPublishError")}
        ]);

        const request = await Network.postJson(ProductType.COMMUNITY, "/community/images/publish", data, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }
}